import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { base44 } from "api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  format, startOfMonth, getDaysInMonth,
  addMonths, subMonths,
} from "date-fns";
import { convertQuantityToInventoryUnit } from "../utils/unitConversion";
import { parseLocaleNumber } from "../utils/decimal";

export default function useFinanceData() {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(new Date());

  const { data: sales = [] } = useQuery({
    queryKey: ["sales"],
    queryFn: () => base44.entities.Sale.list("-date"),
    initialData: [],
  });

  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses"],
    queryFn: () => base44.entities.Expense.list("-date"),
    initialData: [],
  });

  const { data: shifts = [] } = useQuery({
    queryKey: ["shifts"],
    queryFn: () => base44.entities.Shift.list(),
    initialData: [],
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: () => base44.entities.Employee.list(),
    initialData: [],
  });

  const { data: restaurant } = useQuery({
    queryKey: ["restaurant"],
    queryFn: async () => {
      const data = await base44.entities.Restaurant.list();
      return data[0];
    },
  });

  const { data: recipes = [] } = useQuery({
    queryKey: ["recipes"],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const { data: inventory = [] } = useQuery({
    queryKey: ["inventory"],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const now = useMemo(() => new Date(), []);
  const todayStr = format(now, "yyyy-MM-dd");
  const currentMonth = selectedMonth.getMonth();
  const currentYear = selectedMonth.getFullYear();
  const daysInMonth = getDaysInMonth(selectedMonth);
  const currentDayOfMonth = now.getDate();

  const isCurrentMonth = now.getMonth() === currentMonth && now.getFullYear() === currentYear;
  const isFutureMonth = selectedMonth > now;
  const isPastMonth = selectedMonth < startOfMonth(now);

  // Opening days based on restaurant settings
  const { totalOpenDays, remainingOpenDays } = useMemo(() => {
    const calendarFallback = {
      totalOpenDays: daysInMonth,
      remainingOpenDays: Math.max(daysInMonth - currentDayOfMonth, 0),
    };
    if (!restaurant || !restaurant.opening_days || restaurant.opening_days.length === 0) {
      return calendarFallback;
    }

    // Match opening_days against multiple representations so the projection
    // doesn't collapse when the stored value is in a different locale than the UI.
    const normalize = (s) => (s == null ? '' : String(s).trim().toLowerCase());
    const allowed = new Set(
      restaurant.opening_days.map(normalize).filter(Boolean)
    );
    const dayKeys = [
      ['sunday', 'sonntag'],
      ['monday', 'montag'],
      ['tuesday', 'dienstag'],
      ['wednesday', 'mittwoch'],
      ['thursday', 'donnerstag'],
      ['friday', 'freitag'],
      ['saturday', 'samstag'],
    ];
    const isOpenWeekday = (weekday) => {
      const variants = [
        ...dayKeys[weekday],
        normalize(t(`setup.${dayKeys[weekday][0]}`)),
      ];
      return variants.some((v) => v && allowed.has(v));
    };

    let total = 0;
    let remaining = 0;
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      if (isOpenWeekday(date.getDay())) {
        total++;
        if (day > currentDayOfMonth) remaining++;
      }
    }

    // If the stored opening_days didn't match any weekday (locale or data drift),
    // fall back to the full calendar so projections still make sense.
    if (total === 0) return calendarFallback;

    return { totalOpenDays: total, remainingOpenDays: remaining };
  }, [restaurant, daysInMonth, currentDayOfMonth, currentYear, currentMonth, t]);

  const openDaysPassed = isFutureMonth ? 0 : (isPastMonth ? totalOpenDays : totalOpenDays - remainingOpenDays);

  // Filter sales/expenses by month
  const currentMonthSales = useMemo(
    () => sales.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [sales, currentMonth, currentYear]
  );

  const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  const lastMonthSales = useMemo(
    () => sales.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    }),
    [sales, lastMonth, lastMonthYear]
  );

  const currentMonthExpenses = useMemo(
    () => expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [expenses, currentMonth, currentYear]
  );
  const comparisonLastMonthExpensesList = useMemo(() => {
    const monthExpenses = expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    if (!isCurrentMonth) return monthExpenses;

    return monthExpenses.filter((expense) => {
      const expenseDate = new Date(expense.date);
      return expenseDate.getDate() <= currentDayOfMonth;
    });
  }, [currentDayOfMonth, expenses, isCurrentMonth, lastMonth, lastMonthYear]);

  const observedSalesDays = useMemo(() => {
    if (isFutureMonth) return 0;

    const saleDates = currentMonthSales
      .filter((sale) => isPastMonth || sale.date <= todayStr)
      .map((sale) => sale.date);

    return new Set(saleDates).size;
  }, [currentMonthSales, isFutureMonth, isPastMonth, todayStr]);

  // Revenue totals
  const currentMonthRevenue = useMemo(
    () => currentMonthSales.reduce((sum, s) => sum + (s.total_amount || 0), 0),
    [currentMonthSales]
  );
  const lastMonthRevenue = useMemo(
    () => lastMonthSales.reduce((sum, s) => sum + (s.total_amount || 0), 0),
    [lastMonthSales]
  );
  const comparisonLastMonthRevenue = useMemo(() => {
    if (!isCurrentMonth) return lastMonthRevenue;

    return lastMonthSales
      .filter((sale) => {
        const saleDate = new Date(sale.date);
        return saleDate.getDate() <= currentDayOfMonth;
      })
      .reduce((sum, sale) => sum + (sale.total_amount || 0), 0);
  }, [currentDayOfMonth, isCurrentMonth, lastMonthRevenue, lastMonthSales]);
  const comparisonLastMonthSales = useMemo(() => {
    if (!isCurrentMonth) return lastMonthSales;

    return lastMonthSales.filter((sale) => {
      const saleDate = new Date(sale.date);
      return saleDate.getDate() <= currentDayOfMonth;
    });
  }, [currentDayOfMonth, isCurrentMonth, lastMonthSales]);
  const currentMonthGuests = useMemo(
    () => currentMonthSales.reduce((sum, s) => sum + (s.guests_count || 0), 0),
    [currentMonthSales]
  );
  const comparisonLastMonthGuests = useMemo(
    () => comparisonLastMonthSales.reduce((sum, sale) => sum + (sale.guests_count || 0), 0),
    [comparisonLastMonthSales]
  );

  // COGS from Wareneinkauf
  const currentMonthCogs = useMemo(
    () => currentMonthExpenses
      .filter(e => e.category === "Wareneinkauf")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [currentMonthExpenses]
  );

  const lastMonthCogs = useMemo(() => {
    const lastExpenses = expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });
    return lastExpenses
      .filter(e => e.category === "Wareneinkauf")
      .reduce((sum, e) => sum + (e.amount || 0), 0);
  }, [expenses, lastMonth, lastMonthYear]);

  // Average product cost rate: when sales aren't linked to recipes (so we can't
  // compute COGS per sale), fall back to the typical material-cost share
  // implied by the recipe book — average of (material cost / price) across
  // recipes that have both a price and resolvable ingredient costs.
  const productCogsRate = useMemo(() => {
    const ratios = [];
    for (const recipe of recipes) {
      const price = parseLocaleNumber(recipe.price) || 0;
      if (price <= 0 || !recipe.ingredients || recipe.ingredients.length === 0) continue;
      let materialTotal = 0;
      for (const ing of recipe.ingredients) {
        const invItem = inventory.find(
          (item) => item.id === ing.inventory_item_id || item.name === ing.inventory_item_name
        );
        if (!invItem || !invItem.cost_per_unit) continue;
        const qty = parseLocaleNumber(ing.quantity_needed) || 0;
        const convertedQty = convertQuantityToInventoryUnit(qty, ing.unit, invItem.unit);
        materialTotal += convertedQty * parseLocaleNumber(invItem.cost_per_unit);
      }
      if (materialTotal <= 0) continue;
      const servings = parseLocaleNumber(recipe.servings) || 1;
      const materialPerServing = materialTotal / servings;
      ratios.push(materialPerServing / price);
    }
    if (ratios.length === 0) return null;
    return ratios.reduce((a, b) => a + b, 0) / ratios.length;
  }, [recipes, inventory]);

  // Historical COGS rate for forecasting
  const historicalCogsRate = useMemo(() => {
    if (currentMonthRevenue > 0 && currentMonthCogs > 0) return currentMonthCogs / currentMonthRevenue;
    if (lastMonthRevenue > 0 && lastMonthCogs > 0) return lastMonthCogs / lastMonthRevenue;
    if (productCogsRate !== null) return productCogsRate;
    return 0.30;
  }, [currentMonthRevenue, currentMonthCogs, lastMonthRevenue, lastMonthCogs, productCogsRate]);

  // Labor costs from shifts
  const currentMonthShifts = useMemo(
    () => shifts.filter(s => {
      const d = new Date(s.date);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }),
    [shifts, currentMonth, currentYear]
  );
  const comparisonLastMonthShifts = useMemo(() => {
    const monthShifts = shifts.filter((shift) => {
      const d = new Date(shift.date);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    if (!isCurrentMonth) return monthShifts;

    return monthShifts.filter((shift) => {
      const shiftDate = new Date(shift.date);
      return shiftDate.getDate() <= currentDayOfMonth;
    });
  }, [currentDayOfMonth, isCurrentMonth, lastMonth, lastMonthYear, shifts]);

  const calcShiftHours = (shift, employee) => {
    let hours = 8;
    if (shift.shift_type === "Mittag" && employee.shift_start_lunch && employee.shift_end_lunch) {
      const [sH, sM] = employee.shift_start_lunch.split(":").map(Number);
      const [eH, eM] = employee.shift_end_lunch.split(":").map(Number);
      hours = (eH + eM / 60) - (sH + sM / 60);
    } else if (shift.shift_type === "Abend" && employee.shift_start_dinner && employee.shift_end_dinner) {
      const [sH, sM] = employee.shift_start_dinner.split(":").map(Number);
      const [eH, eM] = employee.shift_end_dinner.split(":").map(Number);
      hours = (eH + eM / 60) - (sH + sM / 60);
    }
    return hours;
  };

  const currentMonthLaborCost = useMemo(() => {
    let total = 0;
    currentMonthShifts.forEach(shift => {
      const emp = employees.find(e => e.id === shift.employee_id);
      if (emp?.hourly_rate) {
        total += emp.hourly_rate * calcShiftHours(shift, emp);
      }
    });
    return total;
  }, [currentMonthShifts, employees]);
  const comparisonLastMonthLaborCost = useMemo(() => {
    let total = 0;
    comparisonLastMonthShifts.forEach((shift) => {
      const emp = employees.find(e => e.id === shift.employee_id);
      if (emp?.hourly_rate) {
        total += emp.hourly_rate * calcShiftHours(shift, emp);
      }
    });
    return total;
  }, [comparisonLastMonthShifts, employees]);

  const observedLaborDays = useMemo(() => {
    if (isFutureMonth) return 0;

    const shiftDates = currentMonthShifts
      .filter((shift) => isPastMonth || shift.date <= todayStr)
      .map((shift) => shift.date);

    return new Set(shiftDates).size;
  }, [currentMonthShifts, isFutureMonth, isPastMonth, todayStr]);

  // Other expenses (non-COGS, non-Personal)
  const currentMonthOtherExpenses = useMemo(
    () => currentMonthExpenses
      .filter(e => e.category !== "Wareneinkauf" && e.category !== "Personal")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [currentMonthExpenses]
  );
  const comparisonLastMonthCogs = useMemo(
    () => comparisonLastMonthExpensesList
      .filter(e => e.category === "Wareneinkauf")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [comparisonLastMonthExpensesList]
  );
  const comparisonLastMonthOtherExpenses = useMemo(
    () => comparisonLastMonthExpensesList
      .filter(e => e.category !== "Wareneinkauf" && e.category !== "Personal")
      .reduce((sum, e) => sum + (e.amount || 0), 0),
    [comparisonLastMonthExpensesList]
  );

  // Expense projections (recurring vs variable)
  const expenseProjection = useMemo(() => {
    const other = currentMonthExpenses.filter(
      e => e.category !== "Wareneinkauf" && e.category !== "Personal"
    );
    const monthlyRecurring = other
      .filter(e => e.is_recurring && e.recurring_period === "Monatlich")
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const variable = other
      .filter(e => !(e.is_recurring && e.recurring_period === "Monatlich"))
      .reduce((sum, e) => sum + (e.amount || 0), 0);
    const avgVarPerDay = openDaysPassed > 0 ? variable / openDaysPassed : 0;
    const projectedRemainingVar = avgVarPerDay * remainingOpenDays;

    return {
      currentMonthly: monthlyRecurring,
      currentVariable: variable,
      projectedRemainingVariable: projectedRemainingVar,
      projectedTotalOther: monthlyRecurring + variable + projectedRemainingVar,
    };
  }, [currentMonthExpenses, openDaysPassed, remainingOpenDays]);

  // Averages
  const avgRevenuePerDay = isFutureMonth
    ? 0 // simplified: no forecast for future months
    : (observedSalesDays > 0 ? currentMonthRevenue / observedSalesDays : 0);

  const avgLaborPerDay = observedLaborDays > 0 ? currentMonthLaborCost / observedLaborDays : 0;

  // Projected labor (planned shifts + avg for unplanned)
  const projectedTotalLabor = useMemo(() => {
    const dayNames = ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"];

    const futureDays = [];
    for (let day = currentDayOfMonth + 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const dayName = dayNames[date.getDay()];
      if (!restaurant?.opening_days || restaurant.opening_days.includes(dayName)) {
        futureDays.push(format(date, "yyyy-MM-dd"));
      }
    }

    let plannedCost = 0;
    let plannedDays = 0;
    futureDays.forEach(dateStr => {
      const dayShifts = shifts.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      if (dayShifts.length > 0) {
        plannedDays++;
        dayShifts.forEach(shift => {
          const emp = employees.find(e => e.id === shift.employee_id);
          if (emp?.hourly_rate) {
            plannedCost += emp.hourly_rate * calcShiftHours(shift, emp);
          }
        });
      }
    });

    const unplannedDays = remainingOpenDays - plannedDays;
    const unplannedCost = unplannedDays > 0 ? avgLaborPerDay * unplannedDays : 0;
    return currentMonthLaborCost + plannedCost + unplannedCost;
  }, [currentMonthLaborCost, shifts, employees, restaurant, currentDayOfMonth, daysInMonth, currentYear, currentMonth, remainingOpenDays, avgLaborPerDay]);

  // Projections
  const projectedRemainingRevenue = avgRevenuePerDay * remainingOpenDays;
  const projectedTotalRevenue = currentMonthRevenue + projectedRemainingRevenue;
  const projectedTotalCogs = (currentMonthCogs > 0 ? currentMonthCogs : currentMonthRevenue * historicalCogsRate) + (projectedRemainingRevenue * historicalCogsRate);
  const projectedTotalExpenses = projectedTotalCogs + projectedTotalLabor + expenseProjection.projectedTotalOther;
  const projectedTotalProfit = projectedTotalRevenue - projectedTotalExpenses;
  const projectedProfitMargin = projectedTotalRevenue > 0
    ? (projectedTotalProfit / projectedTotalRevenue) * 100
    : 0;

  // Current actuals — fall back to revenue × historical COGS rate when no expenses
  // are recorded yet, matching the daily chart's per-day fallback so the KPI and
  // chart agree.
  const effectiveCurrentMonthCogs = currentMonthCogs > 0
    ? currentMonthCogs
    : currentMonthRevenue * historicalCogsRate;
  const totalExpenses = effectiveCurrentMonthCogs + currentMonthLaborCost + currentMonthOtherExpenses;
  const currentProfit = currentMonthRevenue - totalExpenses;
  const currentProfitMargin = currentMonthRevenue > 0
    ? (currentProfit / currentMonthRevenue) * 100
    : 0;
  const effectiveComparisonCogs = comparisonLastMonthCogs > 0
    ? comparisonLastMonthCogs
    : comparisonLastMonthRevenue * historicalCogsRate;
  const comparisonLastMonthTotalExpenses = effectiveComparisonCogs + comparisonLastMonthLaborCost + comparisonLastMonthOtherExpenses;
  const comparisonLastMonthProfit = comparisonLastMonthRevenue - comparisonLastMonthTotalExpenses;

  // MoM
  const revenueMoM = comparisonLastMonthRevenue > 0
    ? ((currentMonthRevenue - comparisonLastMonthRevenue) / comparisonLastMonthRevenue) * 100
    : 0;
  const expensesMoM = comparisonLastMonthTotalExpenses > 0
    ? ((totalExpenses - comparisonLastMonthTotalExpenses) / comparisonLastMonthTotalExpenses) * 100
    : 0;
  const guestsMoM = comparisonLastMonthGuests > 0
    ? ((currentMonthGuests - comparisonLastMonthGuests) / comparisonLastMonthGuests) * 100
    : 0;
  const profitMoM = comparisonLastMonthProfit > 0
    ? ((currentProfit - comparisonLastMonthProfit) / comparisonLastMonthProfit) * 100
    : 0;

  // Daily chart data
  const dailyData = useMemo(() => {
    const data = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = format(new Date(currentYear, currentMonth, day), "yyyy-MM-dd");
      const daySales = currentMonthSales.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      const dayRevenue = daySales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
      const dayGuests = daySales.reduce((sum, s) => sum + (s.guests_count || 0), 0);

      const dayExpenses = currentMonthExpenses.filter(e => format(new Date(e.date), "yyyy-MM-dd") === dateStr);
      const dayCogs = dayExpenses
        .filter(e => e.category === "Wareneinkauf")
        .reduce((sum, e) => sum + (e.amount || 0), 0);
      const dayOther = dayExpenses
        .filter(e => e.category !== "Wareneinkauf" && e.category !== "Personal")
        .reduce((sum, e) => sum + (e.amount || 0), 0);

      const dayShifts = currentMonthShifts.filter(s => format(new Date(s.date), "yyyy-MM-dd") === dateStr);
      let dayLabor = 0;
      const shiftDetails = [];
      dayShifts.forEach(shift => {
        const emp = employees.find(e => e.id === shift.employee_id);
        if (emp?.hourly_rate) {
          const hours = calcShiftHours(shift, emp);
          const cost = emp.hourly_rate * hours;
          dayLabor += cost;
          shiftDetails.push({
            employee: emp.name,
            shift: shift.shift_type,
            hours: hours.toFixed(1),
            rate: emp.hourly_rate,
            cost: cost.toFixed(2),
          });
        }
      });

      const dailyFixed = totalOpenDays > 0 ? expenseProjection.currentMonthly / totalOpenDays : 0;
      const actualOrEstCogs = dayCogs > 0 ? dayCogs : dayRevenue * historicalCogsRate;
      const totalDayCosts = actualOrEstCogs + dayLabor + dayOther + dailyFixed;

      data.push({
        day,
        date: dateStr,
        revenue: dayRevenue,
        guests: dayGuests,
        costs: totalDayCosts,
        profit: dayRevenue - totalDayCosts,
        cogs: actualOrEstCogs,
        laborCost: dayLabor,
        otherExpenses: dayOther,
        fixedCosts: dailyFixed,
        shifts: shiftDetails,
        expenseDetails: dayExpenses,
        isFuture: new Date(currentYear, currentMonth, day) > now,
      });
    }
    return data;
  }, [currentMonthSales, currentMonthExpenses, currentMonthShifts, employees, daysInMonth, currentYear, currentMonth, totalOpenDays, expenseProjection.currentMonthly, historicalCogsRate, now]);

  return {
    // Month nav
    selectedMonth,
    setSelectedMonth,
    prevMonth: () => setSelectedMonth(subMonths(selectedMonth, 1)),
    nextMonth: () => setSelectedMonth(addMonths(selectedMonth, 1)),
    isCurrentMonth,
    isFutureMonth,
    isPastMonth,

    // Raw data (for expense CRUD)
    expenses,
    sales,
    currentMonthExpenses,

    // Current month actuals
    revenue: currentMonthRevenue,
    totalExpenses,
    profit: currentProfit,
    profitMargin: currentProfitMargin,
    guests: currentMonthGuests,

    // MoM
    revenueMoM,
    expensesMoM,
    guestsMoM,
    profitMoM,
    lastMonthRevenue,
    comparisonLastMonthRevenue,
    comparisonLastMonthGuests,
    comparisonLastMonthTotalExpenses,
    comparisonLastMonthProfit,

    // Forecast
    projectedRevenue: projectedTotalRevenue,
    projectedExpenses: projectedTotalExpenses,
    projectedProfit: projectedTotalProfit,
    projectedProfitMargin,

    // Opening days
    totalOpenDays,
    openDaysPassed,
    remainingOpenDays,

    // Chart
    dailyData,

    // For day detail dialog
    historicalCogsRate,
    historicalLaborRate: currentMonthRevenue > 0 && currentMonthLaborCost > 0
      ? currentMonthLaborCost / currentMonthRevenue
      : 0.25,
  };
}
